import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AppFooter } from '../branding/AppFooter';
import { AppHeader } from '../branding/AppHeader';
import { Button } from '../components/Button/Button';
import * as authApi from './authApi';
import './ParentConsentPage.css';

/**
 * Page shell that wraps every render branch (loading, error,
 * decided, pending) in the shared BytaApps chrome. The parent isn't
 * necessarily signed in — they got here from an email — but they
 * should still see the brand so the "say yes to share" decision
 * isn't on a bare page.
 */
function ConsentShell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="bl-page">
      <AppHeader privacy="published" />
      {children}
      <AppFooter />
    </div>
  );
}

/**
 * Magic-link landing page for parent-consent decisions.
 *
 * The token IS the auth — no signin required. The parent clicks a
 * link from email, sees the kid's submission summary, and chooses
 * Approve or Deny.
 *
 * Three rendered states:
 *  - loading the initial summary
 *  - pending decision: shows project + kid name + Approve/Deny
 *  - already-decided: shows the outcome and a "back to studio" link
 *  - error: link expired / not valid / kid deleted, etc.
 */
export function ParentConsentPage(): JSX.Element {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const summary = useQuery({
    queryKey: ['parent-consent', token],
    queryFn: () => authApi.viewParentConsent(token),
    enabled: token.length > 0,
    retry: false,
  });

  const decision = useMutation({
    mutationFn: (which: 'approve' | 'deny') => authApi.decideParentConsent(token, which),
    onSuccess: (data) => {
      qc.setQueryData(['parent-consent', token], data);
    },
  });

  // Reset decision-mutation error on summary refetch so a stale
  // 409 from a previous click doesn't sit on screen.
  const [, setRefreshKey] = useState(0);
  useEffect(() => {
    setRefreshKey((n) => n + 1);
  }, [summary.data?.decision]);

  if (!token) {
    return (
      <ConsentShell>
        <main className="bl-consent">
          <h1>This link is missing its token.</h1>
          <Button onClick={() => navigate('/')}>Go home</Button>
        </main>
      </ConsentShell>
    );
  }

  if (summary.isPending) {
    return (
      <ConsentShell>
        <main className="bl-consent">
          <p className="bl-consent__status" aria-live="polite">
            Loading…
          </p>
        </main>
      </ConsentShell>
    );
  }

  if (summary.error) {
    return (
      <ConsentShell>
        <main className="bl-consent">
          <h1>This link isn&rsquo;t valid.</h1>
          <p className="bl-consent__lead">{summary.error.message}</p>
          <p className="bl-consent__lead">
            Ask the kid to start a fresh publish from their studio — that will email you a new link.
          </p>
          <Button onClick={() => navigate('/')}>Go home</Button>
        </main>
      </ConsentShell>
    );
  }

  const data = summary.data;
  if (!data) {
    return (
      <ConsentShell>
        <main className="bl-consent">
          <p className="bl-consent__status">Loading…</p>
        </main>
      </ConsentShell>
    );
  }

  if (data.decision !== 'pending') {
    return (
      <ConsentShell>
        <main className="bl-consent">
          <h1>You already {data.decision} this.</h1>
          <p className="bl-consent__lead">
            {data.child_display_name} tried to share their <em>{data.project_name}</em> (
            {data.project_kind} project) to the gallery.
            {data.decision === 'approved' &&
              ' You said yes — it will appear publicly once a grown-up at BytaApps finishes the safety review.'}
            {data.decision === 'denied' && ' You said no — it stays private.'}
            {data.decision === 'expired' &&
              ' The link timed out. Ask the kid to start over if you change your mind.'}
          </p>
          <Button onClick={() => navigate('/studio')}>Open the studio</Button>
        </main>
      </ConsentShell>
    );
  }

  return (
    <ConsentShell>
      <main className="bl-consent">
        <h1>Your kid wants to share something.</h1>
        <p className="bl-consent__lead">
          <strong>{data.child_display_name}</strong> made{' '}
          {/^[aeiou]/i.test(data.project_kind) ? 'an' : 'a'} {data.project_kind} project called{' '}
          <em>{data.project_name}</em> and wants to publish it to the gallery.
        </p>
        <p className="bl-consent__lead">If you say yes:</p>
        <ul className="bl-consent__list">
          <li>One sample picture per class is sent to a grown-up at BytaApps for safety review.</li>
          <li>
            Once cleared, the project shows up in the public gallery — anyone can browse and remix
            it.
          </li>
          <li>You can ask us to take it down anytime.</li>
        </ul>
        <p className="bl-consent__lead">If you say no, nothing leaves their device.</p>

        <div className="bl-consent__actions">
          <Button onClick={() => decision.mutate('approve')} disabled={decision.isPending}>
            {decision.isPending ? 'Sending…' : 'Yes, share it'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => decision.mutate('deny')}
            disabled={decision.isPending}
          >
            No, keep it private
          </Button>
        </div>

        {decision.error && (
          <p className="bl-consent__error" role="alert">
            {decision.error.message}
          </p>
        )}
      </main>
    </ConsentShell>
  );
}
